import type { CanvasCard, DataTableCardData, PageLayout, ReportPage, TableCardConfig, TableRow } from "store/types";
import { GRID_CONSTANTS } from "store/types/reports/report-generator";

export interface SplitPageMeta extends ReportPage {
  isContinuation: boolean;
  isSpillover?: boolean;
  // The table ends on this page with room below. It renders as an editable grid
  // (drop zone under the table) in the canvas, so users can drop components after
  // the table; those flow onto following pages as usual.
  isTableEnd?: boolean;
  originalPageIndex: number;
}

/** Mirrors TIER_STYLES from DataTableCard — used to estimate row heights. */
export const TABLE_TIER_METRICS: Record<string, { bodyFont: number; headerFont: number; padV: number; padH: number }> =
  {
    F: { bodyFont: 13, headerFont: 11, padV: 10, padH: 10 },
    M: { bodyFont: 11, headerFont: 9, padV: 8, padH: 12 },
    S: { bodyFont: 9, headerFont: 8, padV: 6, padH: 9 }
  };

export function getTableTier(widthPercent: number): string {
  if (widthPercent >= 60) return "F";
  if (widthPercent >= 38) return "M";
  return "S";
}

/** Width consumed by expand/collapse toggle icon (14px) + flex gap (6px). */
const TOGGLE_ICON_WIDTH = 20;
/** Extra left padding per nesting level for child rows (matches DataTableCard). */
const INDENT_PER_DEPTH = 12;
/**
 * Minimum content height of the expand/collapse toggle icon rendered in edit mode.
 * The icon uses fontSize:12 + lineHeight:1.5 → 18px, which exceeds text height at
 * S (14px) and M (17px) tiers, adding 4px and 1px respectively per parent row.
 */
export const TOGGLE_ICON_MIN_H = Math.ceil(12 * 1.5);

/**
 * Estimates a single row's pixel height based on actual cell content.
 * Finds the cell with the most text wrapping and uses it to determine
 * the row height. This is critical for medium/small cards where columns
 * are narrow and text wraps heavily.
 *
 * `colTextWidths` can be a single number (uniform) or an array (per-column).
 * `depth` accounts for child-row indentation and parent-row toggle icons
 * that reduce the available text width in the first column.
 */
export function estimateRowH(
  row: TableRow,
  colKeys: string[],
  colTextWidths: number | number[],
  charWidth: number,
  singleLineH: number,
  padV: number,
  depth: number = 0,
  parentIconMinH: number = 0
): number {
  // Only the first column (Name) wraps text. Data columns use white-space: nowrap,
  // so they are always 1 line regardless of content length.
  let maxLines = 1;
  const title = row.title || "";
  if (title.length > 1) {
    let tw = typeof colTextWidths === "number" ? colTextWidths : colTextWidths[0];
    // Child rows have extra left padding: depth * 12px (matches DataTableCard indentation)
    if (depth > 0) tw -= depth * INDENT_PER_DEPTH;
    // Parent rows with children render an expand/collapse toggle that reduces text width.
    // Only applied in edit mode (parentIconMinH > 0) since preview mode doesn't show the icon.
    if (parentIconMinH > 0 && row.children?.length) tw -= TOGGLE_ICON_WIDTH;
    tw = Math.max(tw, 1);

    // Use word-boundary wrapping estimation to match CSS whiteSpace:"normal" +
    // wordBreak:"normal" rendering. CSS also breaks at hyphens, so
    // "Fixed-Income" can wrap as "Fixed-" / "Income". Split on both spaces
    // and after hyphens (lookbehind keeps the hyphen with the left part).
    if (title.includes(" ") || title.includes("-")) {
      const words = title.split(/(?<=-)|\s+/).filter((w) => w.length > 0);
      // A space is much narrower than an average glyph (~0.3em vs ~0.55em), so
      // charge a reduced width for the inter-word gap. Charging a full charWidth
      // here over-counts wrapped lines for space-heavy names ("really really …"),
      // which inflated table heights and left big gaps at the bottom of pages.
      const spaceWidth = charWidth * 0.5;
      let usedWidth = 0;
      let lines = 1;
      for (const word of words) {
        const wordW = word.length * charWidth;
        if (usedWidth > 0 && usedWidth + spaceWidth + wordW > tw) {
          lines++;
          usedWidth = wordW;
        } else {
          usedWidth += (usedWidth > 0 ? spaceWidth : 0) + wordW;
        }
      }
      maxLines = Math.max(maxLines, lines);
    } else {
      // Single word without hyphens — CSS won't break it, so it's always 1 line
      // (may overflow the cell but doesn't increase row height)
      maxLines = Math.max(maxLines, 1);
    }
  }

  // In edit mode, parent rows render a toggle icon whose height (18px) can exceed
  // the text line height at S (14px) and M (17px) tiers.
  let contentH = Math.ceil(singleLineH * maxLines);
  if (parentIconMinH > 0 && row.children?.length) {
    contentH = Math.max(contentH, parentIconMinH);
  }

  // +1 for the row border, +2 safety buffer for sub-pixel rounding,
  // border-collapse adjustments, and font metric variations.
  return contentH + 2 * padV + 3;
}

export function getHeaderRowH(
  headerFont: number,
  padV: number,
  colKeys?: string[],
  colTitles?: string[],
  colTextWidths?: number | number[]
): number {
  const singleLineH = Math.ceil(headerFont * 1.5);
  if (!colKeys || !colTitles || colTextWidths == null) {
    return singleLineH + 2 * padV + 2;
  }
  // Headers use word-break: normal + overflow: hidden — only multi-word
  // titles wrap (at spaces). Single-word titles stay on one line.
  const charWidth = headerFont * 0.65; // uppercase tends wider
  let maxLines = 1;
  for (let i = 0; i < colKeys.length; i++) {
    const text = colTitles[i] || colKeys[i] || "";
    if (!text.includes(" ") && !text.includes("-")) continue; // single-word headers don't wrap
    const tw = typeof colTextWidths === "number" ? colTextWidths : colTextWidths[i];
    // Split on spaces and hyphens (CSS wordBreak:"normal" breaks at hyphens)
    const words = text.split(/(?<=-)|\s+/).filter((w) => w.length > 0);
    const spaceWidth = charWidth * 0.5; // see estimateRowH — spaces are narrower than glyphs
    let usedWidth = 0;
    let lines = 1;
    for (const word of words) {
      const wordW = word.length * charWidth;
      if (usedWidth > 0 && usedWidth + spaceWidth + wordW > tw) {
        lines++;
        usedWidth = wordW;
      } else {
        usedWidth += (usedWidth > 0 ? spaceWidth : 0) + wordW;
      }
    }
    maxLines = Math.max(maxLines, lines);
  }
  return Math.ceil(singleLineH * maxLines) + 2 * padV + 2;
}

// ─── Expand-aware height helpers ────────────────────────────────────────────

const EMPTY_SET: Set<string> = new Set();
const EMPTY_OFFSETS: Record<string, number> = {};

/**
 * Sum pixel heights of visible rows including children of expanded rows.
 * `depth` tracks nesting level so `rowHFn` can account for indentation.
 */
export function measureVisibleHeight(
  rows: TableRow[],
  expanded: Set<string>,
  parentKey: string,
  offsets: Record<string, number>,
  rowHFn: (row: TableRow, depth: number) => number,
  depth: number = 0
): number {
  const offset = offsets[parentKey] ?? 0;
  let total = 0;
  for (let i = 0; i < rows.length; i++) {
    const rowKey = `${parentKey}-${i + offset}`;
    total += rowHFn(rows[i], depth);
    // Tables are always fully expanded — every group's children are visible.
    if (rows[i].children?.length) {
      total += measureVisibleHeight(rows[i].children!, expanded, rowKey, offsets, rowHFn, depth + 1);
    }
  }
  return total;
}

interface SliceResult {
  taken: TableRow[];
  remaining: TableRow[];
  remainingOffsets: Record<string, number>;
}

/**
 * Greedy row flow: walk rows in render order (depth-first, fully expanded) and
 * keep taking them until the next row no longer fits the budget, then break
 * exactly there — splitting a group between its children if that is where the
 * page boundary falls. There is no "keep parent with children" preference: the
 * goal is simply to fill each page and only push a row to the next page when it
 * genuinely doesn't fit at the bottom.
 *
 * `state.force` keeps the first row-chain of a fresh page (parent → … → first
 * leaf) regardless of budget, so a single row taller than a page can never
 * stall pagination (it overflows that one page instead of looping forever).
 */
function sliceByHeight(
  rows: TableRow[],
  budget: number,
  expanded: Set<string>,
  parentKey: string,
  offsets: Record<string, number>,
  rowHFn: (row: TableRow, depth: number) => number,
  depth: number = 0,
  state: { force: boolean } = { force: true }
): SliceResult {
  const topOffset = offsets[parentKey] ?? 0;
  let used = 0;

  for (let i = 0; i < rows.length; i++) {
    const origIdx = i + topOffset;
    const rowKey = `${parentKey}-${origIdx}`;
    const row = rows[i];
    const rowH = rowHFn(row, depth);
    const hasChildren = !!row.children?.length;

    // Once the page holds at least one leaf, break before the first row that
    // overflows. While `state.force` is set we are still placing the mandatory
    // first row-chain of a fresh page, so we take regardless of budget.
    if (!state.force && used + rowH > budget) {
      return {
        taken: rows.slice(0, i),
        remaining: rows.slice(i),
        remainingOffsets: { [parentKey]: origIdx }
      };
    }
    used += rowH;

    if (!hasChildren) {
      state.force = false;
      continue;
    }

    const childResult = sliceByHeight(row.children!, budget - used, expanded, rowKey, offsets, rowHFn, depth + 1, state);

    if (childResult.remaining.length > 0) {
      // The page boundary falls inside this group: keep the children that fit
      // here with the parent, and carry the rest onto the next page. The parent
      // is re-emitted only to preserve child depth — it is flagged as a
      // continuation ghost so its own row is not drawn again (no duplicate header).
      const takenRow = { ...row, children: childResult.taken };
      const remainingRow = { ...row, children: childResult.remaining, isContinuationGroup: true };
      return {
        taken: [...rows.slice(0, i), takenRow],
        remaining: [remainingRow, ...rows.slice(i + 1)],
        remainingOffsets: { [parentKey]: origIdx, ...childResult.remainingOffsets }
      };
    }

    // All children fit on this page — account for their height and continue.
    used += measureVisibleHeight(row.children!, expanded, rowKey, offsets, rowHFn, depth + 1);
  }

  return { taken: rows, remaining: [], remainingOffsets: {} };
}

// ─── Card height estimation ─────────────────────────────────────────────────

/** PreviewCardWrapper adds a 1px border (top + bottom = 2px) per card. */
const CARD_BORDER_PX = 2;

/**
 * Estimate a non-table card's rendered height.
 * Uses grid height (gridH * GRID_CELL_HEIGHT) when available,
 * explicit `heightPx` when set, otherwise falls back to type-based defaults.
 */
function estimateCardHeight(card: CanvasCard): number {
  if (card.gridH > 0) return card.gridH * GRID_CONSTANTS.GRID_CELL_HEIGHT;
  if (card.heightPx != null) return card.heightPx;
  switch (card.config.type) {
    case "kpi":
      return 90;
    case "summary":
      return 110;
    case "text":
      return 120;
    case "chart":
    case "exposure":
      return 240;
    case "cashflow":
    case "performance":
    case "unfunded":
    case "macroBrief":
      return 200;
    case "benchmark":
    case "dataFreshness":
      return 160;
    case "ai_card":
      return 150;
    default:
      return 200;
  }
}

/**
 * Group a flat list of cards into visual rows using the same width-accumulation
 * logic that PageView and PreviewMode use for rendering.
 */
function groupIntoVisualRows(cards: CanvasCard[]): CanvasCard[][] {
  const rows: CanvasCard[][] = [];
  let current: CanvasCard[] = [];
  let w = 0;
  for (const card of cards) {
    if (w > 0 && w + card.widthPercent > 100.01) {
      rows.push(current);
      current = [];
      w = 0;
    }
    current.push(card);
    w += card.widthPercent;
  }
  if (current.length > 0) rows.push(current);
  return rows;
}

// ─── Public API ──────────────────────────────────────────────────────────────

/**
 * Assign a stable canonical `__path` to every row and child of a table tree
 * (e.g. "0", "0.1", "0.1.2"). The path identifies a row independently of which
 * page chunk it ends up in, so its DOM-measured height can be looked up during
 * pagination. Returns a deep-cloned tree; the input is not mutated.
 */
export function assignRowPaths(rows: TableRow[], prefix = ""): TableRow[] {
  return rows.map((row, i) => {
    const path = prefix === "" ? `${i}` : `${prefix}.${i}`;
    return {
      ...row,
      __path: path,
      children: row.children ? assignRowPaths(row.children, path) : row.children
    };
  });
}

export function isTableCardWithData(card: CanvasCard): card is CanvasCard & { data: DataTableCardData } {
  if (card.config.type !== "table" || !card.data) return false;
  const d = card.data as DataTableCardData;
  return Array.isArray(d.data) && Array.isArray(d.columns);
}

export interface SplitResult {
  pages: CanvasCard[][];
  pageMeta: SplitPageMeta[];
  /** Per-card index offsets for DataTableCard key generation. */
  cardOffsets: Record<string, Record<string, number>>;
  /** Maps continuation card IDs back to their original card ID. */
  originalCardIds: Record<string, string>;
}

export function splitPagesForOverflow(
  groupedPages: CanvasCard[][],
  pages: ReportPage[],
  pageHeight: number,
  pageWidth: number,
  expandedRows?: Record<string, Set<string>>,
  options?: {
    contentPadding?: number;
    isEditMode?: boolean;
    // When provided, these return DOM-measured pixel heights for exact pagination.
    // Returning undefined falls back to the heuristic estimate (e.g. before the
    // measurement pass has completed).
    getRowHeight?: (cardId: string, row: TableRow) => number | undefined;
    getHeaderHeight?: (cardId: string) => number | undefined;
    // Extra height reserved on a table's FIRST page for the editable card handle
    // (canvas only). Continuation segments are read-only and reserve nothing.
    tableTopChromePx?: number;
  }
): SplitResult {
  const resolvedPages: CanvasCard[][] = [];
  const resolvedMeta: SplitPageMeta[] = [];
  const cardOffsets: Record<string, Record<string, number>> = {};
  const originalCardIds: Record<string, string> = {};

  for (let pageIdx = 0; pageIdx < groupedPages.length; pageIdx++) {
    const pageCards = groupedPages[pageIdx];
    const page = pages[pageIdx];
    const layout = page.layout;

    if (layout === "title") {
      resolvedPages.push(pageCards);
      resolvedMeta.push({ ...page, isContinuation: false, originalPageIndex: pageIdx });
      continue;
    }

    const headerH = layout === "header-content" ? 180 : layout === "section" ? 150 : 0;
    const footerH = 30; // rendered footer is ~29px (6px pad + ~16px text + 6px pad + 1px border)
    const padVertical = 80; // 32px top + 48px bottom content padding
    const safetyBuffer = 6; // small margin for sub-pixel rounding (heights are DOM-measured when available)
    const rowGap = 20;
    const contentOnlyAvailH = pageHeight - footerH - padVertical - safetyBuffer;

    let currentCards: CanvasCard[] = [];
    let usedH = 0;
    let currentAvailH = pageHeight - headerH - footerH - padVertical - safetyBuffer;
    let currentMeta: SplitPageMeta = { ...page, isContinuation: false, originalPageIndex: pageIdx };
    const cellPlusGap = GRID_CONSTANTS.GRID_CELL_HEIGHT + GRID_CONSTANTS.GAP;
    // When a split table's final chunk lands on a page, this records its grid
    // height so cards placed after it (dropped below the table) can be shifted to
    // sit just under the (much shorter) final chunk instead of at their original
    // full-table offset.
    let tableEndChunkGridH: number | null = null;

    const flushPage = () => {
      if (currentCards.length > 0) {
        resolvedPages.push(currentCards);
        resolvedMeta.push(currentMeta);
      }
      currentCards = [];
      usedH = 0;
      currentAvailH = contentOnlyAvailH;
      currentMeta = { layout: "content" as PageLayout, isContinuation: true, originalPageIndex: pageIdx };
    };

    // Group cards into visual rows so side-by-side cards share one row height.
    // Sort by grid position first so cards are processed in spatial order,
    // preventing non-table cards from being swept onto continuation pages.
    const sortedPageCards = [...pageCards].sort((a, b) => a.gridY - b.gridY || a.gridX - b.gridX);
    const visualRows = groupIntoVisualRows(sortedPageCards);

    for (const visualRow of visualRows) {
      // Check if this row contains a splittable table
      const tableCardIdx = visualRow.findIndex((c) => isTableCardWithData(c));

      if (tableCardIdx === -1) {
        // ── Non-table row: height = tallest card in the row + border ──
        const rowH = Math.max(...visualRow.map((c) => estimateCardHeight(c))) + CARD_BORDER_PX;
        const gap = currentCards.length > 0 ? rowGap : 0;

        if (currentCards.length > 0 && usedH + gap + rowH > currentAvailH) {
          flushPage();
        }
        if (currentCards.length > 0) usedH += rowGap;
        currentCards.push(...visualRow);
        usedH += rowH;
        continue;
      }

      // ── Row contains a table card ──
      const card = visualRow[tableCardIdx];
      const companionCards = visualRow.filter((_, i) => i !== tableCardIdx);
      const companionH =
        companionCards.length > 0 ? Math.max(...companionCards.map((c) => estimateCardHeight(c))) + CARD_BORDER_PX : 0;

      const tableData = (card as CanvasCard & { data: DataTableCardData }).data;
      // Move "Total" row to the end to match DataTableCard rendering order
      const rawRows = tableData.data;
      const totalIdx = rawRows.findIndex((r) => r.title?.toLowerCase() === "total");
      const tableRows =
        totalIdx > -1 && totalIdx < rawRows.length - 1
          ? [...rawRows.slice(0, totalIdx), ...rawRows.slice(totalIdx + 1), rawRows[totalIdx]]
          : rawRows;

      if (tableRows.length === 0) {
        const rowH = Math.max(60, companionH) + CARD_BORDER_PX;
        const gap = currentCards.length > 0 ? rowGap : 0;
        if (currentCards.length > 0 && usedH + gap + rowH > currentAvailH) {
          flushPage();
        }
        if (currentCards.length > 0) usedH += rowGap;
        currentCards.push(...visualRow);
        usedH += rowH;
        continue;
      }

      // Compute per-row height metrics for this table card
      const tier = getTableTier(card.widthPercent);
      const { bodyFont, headerFont, padV, padH } = TABLE_TIER_METRICS[tier];
      const outerPad = options?.contentPadding ?? 96;
      const availableW = pageWidth - outerPad;
      const colW = (availableW - (GRID_CONSTANTS.GRID_COLUMNS - 1) * GRID_CONSTANTS.GAP) / GRID_CONSTANTS.GRID_COLUMNS;
      const cardWidthPx = colW * card.gridW + (card.gridW - 1) * GRID_CONSTANTS.GAP;
      const colKeys = tableData.columns.map((col: string | { key: string }) =>
        typeof col === "string" ? col : col.key
      );
      const colTitles = tableData.columns.map((col: string | { key: string; title?: string }) =>
        typeof col === "string" ? col : col.title || col.key
      );
      const colCount = colKeys.length;
      // Average glyph width factor — must stay in sync with computeTableGridH.
      const charWidth = bodyFont * 0.55;
      const singleLineH = Math.ceil(bodyFont * 1.5);

      // Use actual column widths when available for accurate wrapping estimation
      const storedWidths = (card.config as TableCardConfig).columnWidths;
      let colTextWidths: number | number[];
      if (storedWidths && colKeys.every((k) => storedWidths[k] != null)) {
        const rawPcts = colKeys.map((k) => storedWidths[k]);
        const sum = rawPcts.reduce((a, b) => a + b, 0);
        colTextWidths = rawPcts.map((pct) => Math.max((pct / sum) * cardWidthPx - 2 * padH, 10));
      } else {
        const colWidthPx = cardWidthPx / Math.max(colCount, 1);
        colTextWidths = Math.max(colWidthPx - 2 * padH, 10);
      }

      // No expand/collapse toggle is rendered anymore, so it never affects row height.
      const iconMinH = 0;
      const rowHFn = (r: TableRow, depth: number) => {
        // Continuation ghosts aren't drawn, so they take no height.
        if (r.isContinuationGroup) return 0;
        // Prefer the exact DOM-measured height; fall back to the estimate.
        const measured = r.__path != null ? options?.getRowHeight?.(card.id, r) : undefined;
        if (measured != null) return measured;
        return estimateRowH(r, colKeys, colTextWidths, charWidth, singleLineH, padV, depth, iconMinH);
      };

      const headerRowH =
        options?.getHeaderHeight?.(card.id) ?? getHeaderRowH(headerFont, padV, colKeys, colTitles, colTextWidths);
      const tablePad = 8;
      // Reserve the editable card handle on the table's FIRST page (canvas only).
      const primaryChrome = options?.tableTopChromePx ?? 0;

      const cardExpanded = expandedRows?.[card.id] ?? EMPTY_SET;
      const totalContentH = measureVisibleHeight(tableRows, cardExpanded, "root", EMPTY_OFFSETS, rowHFn);
      const totalTableH = tablePad + headerRowH + totalContentH + CARD_BORDER_PX + primaryChrome;
      const gap = currentCards.length > 0 ? rowGap : 0;
      const spaceForRow = currentAvailH - usedH - gap;

      // If the whole table (and companion cards) fit, add the row
      if (Math.max(totalTableH, companionH) <= spaceForRow) {
        usedH += gap + Math.max(totalTableH, companionH);
        currentCards.push(...visualRow);
        // The table ends on this page without splitting — mark it editable so the
        // canvas exposes a drop zone below it. Cards already keep their layout
        // positions, so no repositioning is needed here.
        currentMeta = { ...currentMeta, isTableEnd: true };
        continue;
      }

      // ── Split required ──
      const spaceForTableRows = spaceForRow - tablePad - headerRowH - CARD_BORDER_PX - primaryChrome;

      let remaining: TableRow[];
      let currentOffsets: Record<string, number> = {};

      if (spaceForTableRows > 0) {
        const sliceResult = sliceByHeight(tableRows, spaceForTableRows, cardExpanded, "root", EMPTY_OFFSETS, rowHFn);
        // Fill the current page with whatever rows fit; only push to the next
        // page when nothing fits at all.
        if (sliceResult.taken.length > 0) {
          // First chunk + companion cards stay on the current page
          currentCards.push(...companionCards);
          currentCards.push({
            ...card,
            data: { columns: tableData.columns, data: sliceResult.taken }
          });
          remaining = sliceResult.remaining;
          currentOffsets = sliceResult.remainingOffsets;
        } else {
          remaining = tableRows;
        }
      } else {
        remaining = tableRows;
      }

      // If nothing fit on this page, companions go with the first chunk on next page
      if (remaining === tableRows) {
        flushPage();
        // Try fitting on the fresh page — reset gridY since this is a new page
        const freshSpace = currentAvailH - tablePad - headerRowH - CARD_BORDER_PX;
        const freshSlice = sliceByHeight(tableRows, freshSpace, cardExpanded, "root", EMPTY_OFFSETS, rowHFn);
        currentCards.push(...companionCards.map((c) => ({ ...c, gridY: 0 })));
        if (freshSlice.taken.length > 0) {
          currentCards.push({
            ...card,
            gridY: 0,
            data: { columns: tableData.columns, data: freshSlice.taken }
          });
          remaining = freshSlice.remaining;
          currentOffsets = freshSlice.remainingOffsets;
        } else {
          // Table doesn't even fit a fresh page — push it all to avoid infinite loop
          currentCards.push({ ...card, gridY: 0 });
          remaining = [];
        }
      } else {
        flushPage();
      }

      let chunkIdx = 1;

      while (remaining.length > 0) {
        const contSpaceForRows = currentAvailH - tablePad - headerRowH - CARD_BORDER_PX;

        const contResult = sliceByHeight(remaining, contSpaceForRows, cardExpanded, "root", currentOffsets, rowHFn);

        const contCardId = `${card.id}-cont-${chunkIdx}`;
        const contCard: CanvasCard = {
          ...card,
          id: contCardId,
          gridY: 0,
          data: { columns: tableData.columns, data: contResult.taken }
        };

        if (Object.keys(currentOffsets).length > 0) {
          cardOffsets[contCardId] = currentOffsets;
        }
        originalCardIds[contCardId] = card.id;

        if (contResult.remaining.length > 0) {
          resolvedPages.push([contCard]);
          resolvedMeta.push({ layout: "content" as PageLayout, isContinuation: true, originalPageIndex: pageIdx });
        } else {
          // Final chunk: keep it on the current page and mark the page as the
          // table's end — editable, with a drop zone below. Subsequent visual
          // rows (cards after the table) keep accumulating onto this same page
          // instead of being pushed to a separate spillover page.
          currentCards.push(contCard);
          const chunkContentH = measureVisibleHeight(contResult.taken, cardExpanded, "root", currentOffsets, rowHFn);
          usedH = tablePad + headerRowH + chunkContentH + CARD_BORDER_PX;
          tableEndChunkGridH = Math.ceil((usedH + GRID_CONSTANTS.GAP) / cellPlusGap);
          currentAvailH = contentOnlyAvailH;
          currentMeta = {
            layout: "content" as PageLayout,
            isContinuation: false,
            isTableEnd: true,
            originalPageIndex: pageIdx
          };
        }

        remaining = contResult.remaining;
        currentOffsets = contResult.remainingOffsets;
        chunkIdx++;
      }
    }

    if (currentCards.length > 0 || (!currentMeta.isContinuation && !currentMeta.isSpillover)) {
      // Reset gridY for spillover pages so cards start from the top
      if (currentMeta.isSpillover && currentCards.length > 0) {
        const minY = Math.min(...currentCards.map((c) => c.gridY));
        if (minY > 0) {
          currentCards = currentCards.map((c) => ({ ...c, gridY: c.gridY - minY }));
        }
      }
      // On a split table's end page, shift cards placed after the table up so the
      // topmost one sits just below the short final chunk (at gridY 0, height
      // tableEndChunkGridH), preserving their relative layout. A single uniform
      // shift keeps cards distinct — clamping each one would collapse several onto
      // the same row and hide all but the last.
      if (currentMeta.isTableEnd && tableEndChunkGridH != null) {
        const postCards = currentCards.filter((c) => !originalCardIds[c.id]);
        if (postCards.length > 0) {
          const minY = Math.min(...postCards.map((c) => c.gridY));
          const shift = tableEndChunkGridH - minY;
          if (shift !== 0) {
            currentCards = currentCards.map((c) =>
              originalCardIds[c.id] ? c : { ...c, gridY: c.gridY + shift }
            );
          }
        }
      }
      resolvedPages.push(currentCards);
      resolvedMeta.push(currentMeta);
    }
  }

  // Post-process: move non-table cards off continuation pages.
  // Continuation pages are display-only artifacts for table overflow — no user cards belong there.
  // When the primary page has a splitting table, stray cards go to a spillover page instead.
  {
    const originalCardMap = new Map<string, CanvasCard>();
    for (const page of groupedPages) {
      for (const card of page) {
        originalCardMap.set(card.id, card);
      }
    }

    const splittingIds = new Set(Object.values(originalCardIds));
    const spilloverByOrigPage = new Map<number, CanvasCard[]>();

    for (let i = resolvedPages.length - 1; i >= 0; i--) {
      if (!resolvedMeta[i].isContinuation) continue;
      const origPageIdx = resolvedMeta[i].originalPageIndex;

      // Cards NOT in originalCardIds are regular user cards (not table overflow chunks)
      const strayCards = resolvedPages[i].filter((c) => !originalCardIds[c.id]);
      if (strayCards.length === 0) continue;

      // Find the primary (non-continuation) page for this originalPageIndex
      const primaryIdx = resolvedMeta.findIndex(
        (m) => m.originalPageIndex === origPageIdx && !m.isContinuation && !m.isSpillover
      );
      if (primaryIdx === -1) continue;

      const hasSplittingTable = resolvedPages[primaryIdx].some((c) => splittingIds.has(c.id));

      for (const stray of strayCards) {
        const original = originalCardMap.get(stray.id);
        const cardToInsert = original ?? stray;

        if (hasSplittingTable) {
          // Collect for spillover page — don't push to primary (no room)
          if (!spilloverByOrigPage.has(origPageIdx)) {
            spilloverByOrigPage.set(origPageIdx, []);
          }
          spilloverByOrigPage.get(origPageIdx)!.push(cardToInsert);
        } else {
          // Check if the card would fit on the primary page before moving it back.
          // In preview mode, different padding/height calculations may cause cards
          // to exceed the visible area and get clipped by overflow:hidden.
          const primaryLayout = resolvedMeta[primaryIdx].layout;
          const pHeaderH = primaryLayout === "header-content" ? 180 : primaryLayout === "section" ? 150 : 0;
          const pAvailH = pageHeight - pHeaderH - 30 - 80 - 6; // footerH + padVertical + safetyBuffer
          const cellPlusGap = GRID_CONSTANTS.GRID_CELL_HEIGHT + GRID_CONSTANTS.GAP;
          const pMaxRows = Math.floor((pAvailH + GRID_CONSTANTS.GAP) / cellPlusGap);

          if (cardToInsert.gridY + cardToInsert.gridH > pMaxRows) {
            if (!spilloverByOrigPage.has(origPageIdx)) {
              spilloverByOrigPage.set(origPageIdx, []);
            }
            spilloverByOrigPage.get(origPageIdx)!.push(cardToInsert);
          } else {
            resolvedPages[primaryIdx].push(cardToInsert);
          }
        }
      }
      resolvedPages[i] = resolvedPages[i].filter((c) => !!originalCardIds[c.id]);

      // Remove empty continuation pages
      if (resolvedPages[i].length === 0) {
        resolvedPages.splice(i, 1);
        resolvedMeta.splice(i, 1);
      }
    }

    // Create spillover pages for stray cards displaced by splitting tables
    for (const [origPageIdx, spillCards] of spilloverByOrigPage) {
      // Reset gridY so cards start from the top of the new page
      const minY = Math.min(...spillCards.map((c) => c.gridY));
      const resetCards = minY > 0 ? spillCards.map((c) => ({ ...c, gridY: c.gridY - minY })) : spillCards;

      // Check if a spillover page already exists for this originalPageIndex
      const existingSpilloverIdx = resolvedMeta.findIndex((m) => m.originalPageIndex === origPageIdx && m.isSpillover);

      if (existingSpilloverIdx !== -1) {
        resolvedPages[existingSpilloverIdx].push(...resetCards);
      } else {
        // Insert after the last page belonging to this originalPageIndex
        let insertAt = resolvedPages.length;
        for (let j = resolvedPages.length - 1; j >= 0; j--) {
          if (resolvedMeta[j].originalPageIndex === origPageIdx) {
            insertAt = j + 1;
            break;
          }
        }

        resolvedPages.splice(insertAt, 0, resetCards);
        resolvedMeta.splice(insertAt, 0, {
          layout: "content" as PageLayout,
          isContinuation: false,
          isSpillover: true,
          originalPageIndex: origPageIdx
        });
      }
    }
  }

  // Final pass: enforce page ordering invariant.
  // Within each originalPageIndex group the order must be: primary → spillover → continuation.
  // This guarantees a table's first segment (on primary or spillover) always precedes
  // its continuation pages, preventing detached continuations from appearing earlier
  // in the document than the component that generated them.
  {
    // primary(0) → spillover(1) → continuation(2) → table-end(3): the table's end
    // page (with cards dropped after it) always follows its continuation chunks.
    const typeOrder = (m: SplitPageMeta) => (m.isTableEnd ? 3 : m.isContinuation ? 2 : m.isSpillover ? 1 : 0);

    const indexed = resolvedMeta.map((meta, i) => ({ i, meta, page: resolvedPages[i] }));
    indexed.sort((a, b) => {
      if (a.meta.originalPageIndex !== b.meta.originalPageIndex) {
        return a.meta.originalPageIndex - b.meta.originalPageIndex;
      }
      const orderDiff = typeOrder(a.meta) - typeOrder(b.meta);
      if (orderDiff !== 0) return orderDiff;
      return a.i - b.i; // preserve original relative order within same type
    });

    for (let k = 0; k < indexed.length; k++) {
      resolvedPages[k] = indexed[k].page;
      resolvedMeta[k] = indexed[k].meta;
    }
  }

  return { pages: resolvedPages, pageMeta: resolvedMeta, cardOffsets, originalCardIds };
}
